/* globals process */
import { Popconfirm, message } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { AiOutlineCheck, AiOutlineClose } from "react-icons/ai"
import styled, { useTheme } from "styled-components"

import AppButton from "../../../_shared/components/AppButton"
import AppTable from "../../../_shared/components/AppTable"
import { useStateValue } from "../../../_shared/context/AppStateStore"
import usePaginatedRestResource from "../../../_shared/hooks/usePaginatedRestResource"
import {
    acceptAgencyRegistrationRequest,
    deleteUser,
    rejectAgencyRegistrationRequest,
    updateManagingRequest,
    updateUser,
} from "../../../api"
import AgencyBottomSheet from "./AgencyBottomSheet"

// ----------------------------------------------------------------------------

function _Agencies(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [managingRequests, reload, , managingRequestsPagination] =
        usePaginatedRestResource("/managing-requests", token)

    const [
        registrationRequests,
        reloadRegistrationRequests,
        ,
        registrationRequestsPagination,
    ] = usePaginatedRestResource("/users", token, {
        filters: { accountStatus: "pending" },
    })

    const [agencies, fetchAgencies, , pagination] = usePaginatedRestResource(
        "/users",
        token,
        {
            filters: { accountStatus: "accepted", role: "agency" },
        }
    )

    const theme = useTheme()

    const [selected, setSelected] = useState()

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function renderStatus(row) {
        const { status } = row
        const backgroundColor =
            status === "accepted" ? theme.colors.success : "white"
        const borderColor =
            status === "accepted" ? theme.colors.success : "black"
        const icon =
            status === "accepted" ? (
                <AiOutlineCheck style={{ fill: "white" }} />
            ) : (
                <AiOutlineClose
                    style={{ fill: "black", width: 20, height: 20 }}
                />
            )

        return (
            <div className="status">
                <div
                    className="icon"
                    style={{
                        backgroundColor,
                        border: `1px solid ${borderColor}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {icon}
                </div>
                <div className="text">
                    {status === "accepted" ? "abilitato" : "disabilitato"}
                </div>
            </div>
        )
    }

    function renderActions(row) {
        const { status } = row
        const isActive = status === "accepted"
        return (
            <div>
                <AppButton
                    uxType={isActive ? "ghosted" : "primary"}
                    htmlType="button"
                    onClick={toggleRequestStatus(row)}
                    className="toggle-btn"
                >
                    {isActive ? "Disabilita" : "Abilita"}
                </AppButton>
            </div>
        )
    }

    function renderRegistrationActions(row) {
        return (
            <div className="actions">
                <AppButton
                    onClick={rejectRegistrationRequest(row)}
                    className="actions-button"
                    uxType="delete"
                >
                    Rifiuta
                </AppButton>
                <AppButton
                    onClick={acceptRegistrationRequest(row)}
                    className="actions-button"
                    uxType="primary"
                >
                    Accetta
                </AppButton>
            </div>
        )
    }

    async function handleAgencyDelete(row) {
        const id = row?.id
        try {
            await deleteUser(id, token)
            fetchAgencies()
            message.success("Agenzia eliminata con successo")
        } catch (e) {
            console.log(e)
            message.error("Qualcosa è andato storto")
        }
    }

    function renderAgencyActions(row) {
        return (
            <div className="actions">
                <AppButton onClick={() => setSelected(row)}>Modifica</AppButton>
                <Popconfirm
                    title={"Sei sicuro di voler eliminare questa agenzia?"}
                    onConfirm={() => handleAgencyDelete(row)}
                >
                    <AppButton className="actions-button" uxType="delete">
                        Elimina
                    </AppButton>
                </Popconfirm>
            </div>
        )
    }

    function acceptRegistrationRequest(request) {
        return async () => {
            try {
                //
                await acceptAgencyRegistrationRequest(request.id, token)
                reloadRegistrationRequests()
                fetchAgencies()
                message.success("Richiesta elaborata con successo")
            } catch (e) {
                console.log(e)
                message.error("Errore durante l'elaborazione della richiesta")
            }
        }
    }

    function rejectRegistrationRequest(request) {
        return async () => {
            try {
                //
                await rejectAgencyRegistrationRequest(request.id, token)
                reloadRegistrationRequests()
                fetchAgencies()
                message.success("Richiesta elaborata con successo")
            } catch (e) {
                console.log(e)
                message.error("Errore durante l'elaborazione della richiesta")
            }
        }
    }

    function toggleRequestStatus(row) {
        return () => {
            const { status, id } = row
            const newStatus = status === "accepted" ? "rejected" : "accepted"
            return updateManagingRequest(id, newStatus, token).then(reload)
        }
    }

    async function handleAgencySubmit(values) {
        try {
            if (!values.id) return
            await updateUser(values.id, { password: values.password }, token)
            message.success("Operazione effettuata")
            setSelected(null)
            fetchAgencies()
        } catch (e) {
            message.error("Qualcosa è andato storto")
            // eslint-disable-next-line
            console.log(e)
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------
    console.log("pagination", registrationRequestsPagination)
    return (
        <div className={`${className}`}>
            <AppTable
                title="Agenzie registrate"
                headers={getRegistrationColumns(renderAgencyActions)}
                rows={agencies}
                pagination={{
                    ...pagination,
                    hideOnSinglePage: false,
                    showTotal: false,
                    position: ["bottomRight", "none"],
                }}
                onChange={(pagination) => fetchAgencies({ pagination })}
            />
            <AppTable
                title="Richieste agenzie"
                headers={getColumns(renderStatus, renderActions)}
                rows={managingRequests}
                className="table"
                pagination={managingRequestsPagination}
                onChange={(pagination) => reload({ pagination })}
            />
            <AppTable
                title="Richieste di registrazione"
                headers={getRegistrationColumns(renderRegistrationActions)}
                rows={registrationRequests}
                className="table"
                pagination={registrationRequestsPagination}
                onChange={(pagination) =>
                    reloadRegistrationRequests({ pagination })
                }
            />
            <AgencyBottomSheet
                open={!!selected}
                defaultValues={selected}
                onCancel={() => setSelected(null)}
                onConfirm={handleAgencySubmit}
            />
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_Agencies.propTypes = {
    className: PropTypes.string.isRequired,
}

_Agencies.defaultProps = {}

// ----------------------------------------------------------------------------

const Agencies = styled(_Agencies)`
    & {
        padding: 30px 60px;

        .table {
            overflow-x: auto;
            .toggle-btn {
                width: 150px;
            }
        }

        .status {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            .icon {
                margin-right: 10px;
                width: 30px;
                height: 30px;
                border-radius: 100%;
            }
            .text {
                font-weight: 700;
            }
        }

        .actions {
            display: flex;
            .actions-button:last-child {
                margin-left: 10px;
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default Agencies

function getColumns(renderStatus, renderActions) {
    return [
        {
            title: "Data",
            key: "tite",
            dataIndex: "createdAt",
            // eslint-disable-next-line
            render: (ca) => (
                <div>
                    <div>{dayjs(ca).format("DD/MM/YYYY")}</div>
                    <div>{dayjs(ca).format("HH:mm")}</div>
                </div>
            ),
        },
        {
            title: "Codice stanza",
            key: "room",
            dataIndex: "room",
            // eslint-disable-next-line
            render: (room) => {
                const baseUrl =
                    process.env.NODE_ENV === "production"
                        ? "https://unalloggio.it"
                        : "http://localhost:3000"
                return (
                    <a
                        href={`${baseUrl}/alloggi/${room?.id}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {room?.id}
                    </a>
                )
            },
        },
        {
            title: "Ragione sociale",
            key: "businessName",
            render: (r) => r?.user?.businessName,
        },
        {
            title: "P. IVA",
            key: "fiscalCode",
            // render: (r) => r?.user?.fiscalCode,
            render: (r) => r?.vatNumber,
        },
        {
            title: "Codice agenzia",
            key: "code",
            render: (r) => r?.user?.id,
        },
        {
            title: "E-mail",
            key: "email",
            render: (r) => r?.user?.email,
        },
        {
            title: "Stato",
            key: "status",
            render: renderStatus,
        },
        {
            title: "",
            key: "action",
            render: renderActions,
        },
    ]
}

function getRegistrationColumns(renderActions) {
    return [
        {
            title: "Data",
            key: "tite",
            dataIndex: "createdAt",
            // eslint-disable-next-line
            render: (ca) => (
                <div>
                    <div>{dayjs(ca).format("DD/MM/YYYY")}</div>
                    <div>{dayjs(ca).format("HH:mm")}</div>
                </div>
            ),
        },
        {
            title: "Ragione sociale",
            key: "businessName",
            render: (r) => r?.businessName,
        },
        {
            title: "P. IVA",
            key: "vatNumber",
            render: (r) => r?.vatNumber,
        },
        {
            title: "Codice agenzia",
            key: "code",
            render: (r) => r?.id,
        },
        {
            title: "E-mail",
            key: "email",
            render: (r) => r?.email,
        },
        ...(renderActions
            ? [
                  {
                      title: "",
                      key: "action",
                      render: renderActions,
                  },
              ]
            : []),
    ]
}
